import { io, Socket } from 'socket.io-client';
import { v4 as uuid } from 'uuid';
import type {
  ServerToClientEvents,
  ClientToServerEvents,
} from '@contracts/ws-events';
import { WS_NAMESPACE, WsEvents } from '@contracts/ws-events';
import type { ChatMessage, SendMessageAck } from '@contracts/messages';
import type { SessionIdentity } from '@contracts/session';
import type { ChatErrorPayload } from '@contracts/errors';
import { useChatStore } from '../store/chat-store';

export type ChatSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

interface JwtConnect {
  mode: 'jwt';
  serverUrl: string;
  token: string;
  /** Optional callback to obtain a fresh token when the current one expires. */
  tokenProvider?: () => Promise<string>;
}

interface AnonConnect {
  mode: 'anonymous';
  serverUrl: string;
  name: string;
  clientSessionId: string;
}

export type ConnectArgs = JwtConnect | AnonConnect;

export class ChatClient {
  private socket: ChatSocket | null = null;
  private currentArgs: ConnectArgs | null = null;
  private refreshing = false;

  connect(args: ConnectArgs): ChatSocket {
    this.disconnect();
    this.currentArgs = args;

    const url = args.serverUrl.replace(/\/$/, '') + WS_NAMESPACE;
    useChatStore.getState().setConn('connecting');

    const auth = args.mode === 'jwt'
      ? { token: args.token }
      : { name: args.name, clientSessionId: args.clientSessionId };

    const sock: ChatSocket = io(url, {
      auth,
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });

    sock.on('connect', () => {
      this.refreshing = false;
      useChatStore.getState().setConn('connected');
    });

    sock.on('disconnect', reason => {
      if (reason === 'io server disconnect') {
        useChatStore.getState().setConn('error', 'Disconnected by server');
      } else {
        useChatStore.getState().setConn('reconnecting');
      }
    });

    sock.on('connect_error', err => {
      const message = err.message || 'Connection error';
      // If the JWT expired or was rejected, try to refresh once via the provider.
      if (
        args.mode === 'jwt' &&
        args.tokenProvider &&
        !this.refreshing &&
        /token|jwt|expired|unauthorized|auth/i.test(message)
      ) {
        this.refreshing = true;
        args.tokenProvider()
          .then(fresh => this.reconnectWithToken(fresh))
          .catch(() => useChatStore.getState().setConn('error', message));
        return;
      }
      useChatStore.getState().setConn('error', message);
    });

    sock.on(WsEvents.SESSION_READY, (identity: SessionIdentity) => {
      useChatStore.getState().setIdentity(identity);
    });

    sock.on(WsEvents.HISTORY_INITIAL, ({ items }: { items: ChatMessage[] }) => {
      useChatStore.getState().setHistory(items);
    });

    sock.on(WsEvents.MESSAGE_NEW, (msg: ChatMessage) => {
      useChatStore.getState().appendMessage(msg);
    });

    sock.on(WsEvents.MESSAGE_REACTION, ({ messageId, reactions }: { messageId: string; reactions: Record<string, string[]> }) => {
      useChatStore.getState().applyReaction(messageId, reactions);
    });

    sock.on(WsEvents.PRESENCE_COUNT, ({ online }: { online: number }) => {
      useChatStore.getState().setOnline(online);
    });

    sock.on(WsEvents.PRESENCE_ONLINE, ({ userId, name }: { userId: string; name: string }) => {
      useChatStore.getState().pushSystemEvent({ kind: 'join', userId, name });
    });

    sock.on(WsEvents.PRESENCE_OFFLINE, ({ userId, name }: { userId: string; name?: string }) => {
      useChatStore.getState().removeTyping(userId);
      if (name) useChatStore.getState().pushSystemEvent({ kind: 'leave', userId, name });
    });

    sock.on(WsEvents.TYPING_USER_STARTED, ({ userId, name }: { userId: string; name: string }) => {
      useChatStore.getState().addTyping(userId, name);
    });

    sock.on(WsEvents.TYPING_USER_STOPPED, ({ userId }: { userId: string }) => {
      useChatStore.getState().removeTyping(userId);
    });

    sock.on(WsEvents.KICKED, ({ reason }: { reason: string }) => {
      useChatStore.getState().setConn('error', `Kicked: ${reason}`);
    });

    sock.on(WsEvents.VIP_GRANTED, () => {
      // Server promoted this user to VIP; clear the locked state. The widget
      // will re-render and the VipChat effect will reconnect with chat enabled.
      useChatStore.getState().setEligibility(false, null);
    });

    sock.on(WsEvents.NOTIF_VIP_ACTIVATED, payload => {
      useChatStore.getState().showActivation(payload);
    });

    sock.on(WsEvents.NOTIF_RATE_LIMITED, payload => {
      useChatStore.getState().setCooldown(payload);
    });

    sock.on(WsEvents.USER_BANNED, payload => {
      useChatStore.getState().setBan(payload);
      // Don't keep reconnecting into a ban.
      if (payload.type !== 'mute') sock.io.opts.reconnection = false;
    });

    sock.on(WsEvents.MESSAGE_BLOCKED, ({ clientNonce, reason }: { clientNonce: string; reason: string }) => {
      // Drop the optimistic message we added on send.
      const state = useChatStore.getState();
      const next = state.messages.filter(m => m.id !== clientNonce);
      useChatStore.setState({ messages: next });
      useChatStore.getState().setBlockedToast(reason);
    });

    sock.on(WsEvents.ERROR, (err: ChatErrorPayload) => {
      // Special case: VIP gate rejection. Capture the progress payload and put
      // the widget in "locked" state instead of marking the connection broken.
      if (err.code === 'NOT_VIP') {
        let progress = null as null | import('@contracts/eligibility').VipProgress;
        const raw = err.details?.progress;
        if (raw) {
          try { progress = JSON.parse(raw); } catch { /* ignore */ }
        }
        useChatStore.getState().setEligibility(true, progress);
        // Don't auto-reconnect into the same rejection.
        sock.io.opts.reconnection = false;
        return;
      }
      useChatStore.getState().setConn('error', err.message);
    });

    this.socket = sock;
    return sock;
  }

  /** Replace the current socket's auth and reconnect, used after a token refresh. */
  private reconnectWithToken(token: string): void {
    if (!this.currentArgs || this.currentArgs.mode !== 'jwt') return;
    this.connect({ ...this.currentArgs, token });
  }

  disconnect(): void {
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = null;
    this.currentArgs = null;
  }

  ackNotificationSeen(notificationId: string): void {
    this.socket?.emit(WsEvents.NOTIF_SEEN, { notificationId });
  }

  sendTypingStart(): void {
    this.socket?.emit(WsEvents.TYPING_START);
  }

  sendTypingStop(): void {
    this.socket?.emit(WsEvents.TYPING_STOP);
  }

  send(text: string, currentUserId: string, currentUserName: string): void {
    if (!this.socket) return;
    const trimmed = text.trim();
    if (!trimmed) return;

    // Pull (and clear) any active reply target so this message quotes it.
    const replyTarget = useChatStore.getState().replyTarget;
    const replyToId = replyTarget && !replyTarget.id.includes('-') ? replyTarget.id : undefined;

    const clientNonce = uuid();
    const optimistic: ChatMessage = {
      id: clientNonce,
      userId: currentUserId,
      userName: currentUserName,
      text: trimmed,
      createdAt: new Date().toISOString(),
      status: 'sent',
      replyTo: replyTarget
        ? {
            id: replyTarget.id,
            userName: replyTarget.userName,
            text: replyTarget.text.length > 140 ? `${replyTarget.text.slice(0, 140)}…` : replyTarget.text,
          }
        : undefined,
    };
    useChatStore.getState().appendMessage(optimistic);
    useChatStore.getState().setReplyTarget(null);

    this.socket.emit(
      WsEvents.MESSAGE_SEND,
      { text: trimmed, clientNonce, replyToId },
      response => {
        if ('error' in response) {
          useChatStore.getState().setConn('error', response.error.message);
          return;
        }
        const ack = response as SendMessageAck;
        useChatStore.getState().reconcileAck(ack.clientNonce, ack.serverId, ack.createdAt);
      },
    );
  }

  /**
   * Upload a recorded voice clip, then send it as a message. Shows an
   * optimistic "uploading" bubble; on failure the bubble flips to an error
   * the user can dismiss/retry. Only works in JWT mode (upload needs the
   * bearer token).
   */
  async sendVoice(
    blob: Blob,
    durationMs: number,
    currentUserId: string,
    currentUserName: string,
  ): Promise<void> {
    if (!this.socket || !this.currentArgs) return;
    if (this.currentArgs.mode !== 'jwt') {
      useChatStore.getState().setBlockedToast('Voice messages require sign-in.');
      return;
    }
    const serverUrl = this.currentArgs.serverUrl.replace(/\/$/, '');
    const token = this.currentArgs.token;
    const clientNonce = uuid();
    const replyTarget = useChatStore.getState().replyTarget;
    const replyToId = replyTarget && !replyTarget.id.includes('-') ? replyTarget.id : undefined;

    // Optimistic bubble in an "uploading" state.
    const localUrl = URL.createObjectURL(blob);
    const optimistic: ChatMessage = {
      id: clientNonce,
      userId: currentUserId,
      userName: currentUserName,
      text: '',
      createdAt: new Date().toISOString(),
      status: 'sent',
      media: { type: 'audio', url: localUrl, durationMs, mimeType: blob.type || 'audio/webm', sizeBytes: blob.size },
      replyTo: replyTarget
        ? { id: replyTarget.id, userName: replyTarget.userName,
            text: replyTarget.text.length > 140 ? `${replyTarget.text.slice(0, 140)}…` : replyTarget.text }
        : undefined,
    };
    useChatStore.getState().appendMessage(optimistic);
    useChatStore.getState().setReplyTarget(null);

    try {
      const form = new FormData();
      const ext = (blob.type.includes('ogg') ? 'ogg' : blob.type.includes('mp4') ? 'm4a' : 'webm');
      form.append('file', blob, `voice.${ext}`);
      form.append('durationMs', String(Math.round(durationMs)));
      const res = await fetch(`${serverUrl}/api/v1/media/voice`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) {
        const msg = res.status === 403
          ? 'Voice messages are disabled.'
          : `Voice upload failed (${res.status}).`;
        throw new Error(msg);
      }
      const up = (await res.json()) as { url: string; durationMs: number };

      this.socket.emit(
        WsEvents.MESSAGE_SEND,
        { text: '', clientNonce, replyToId, mediaUrl: up.url, mediaDurationMs: up.durationMs },
        response => {
          if ('error' in response) {
            useChatStore.getState().setBlockedToast(response.error.message);
            return;
          }
          const ack = response as SendMessageAck;
          useChatStore.getState().reconcileAck(ack.clientNonce, ack.serverId, ack.createdAt);
        },
      );
    } catch (e) {
      // Drop the optimistic bubble + surface the error.
      const st = useChatStore.getState();
      useChatStore.setState({ messages: st.messages.filter(m => m.id !== clientNonce) });
      st.setBlockedToast(e instanceof Error ? e.message : 'Voice upload failed');
      URL.revokeObjectURL(localUrl);
    }
  }

  /** Toggle an emoji reaction on a message. */
  react(messageId: string, emoji: string): void {
    if (!this.socket) return;
    // Skip optimistic-only messages that haven't been persisted yet
    // (their id is a client nonce / uuid, which contains dashes).
    if (messageId.includes('-')) return;
    this.socket.emit(WsEvents.MESSAGE_REACT, { messageId, emoji });
  }
}
